import { memo } from 'react'
import { Button } from "@/shared/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import { Settings, Key, Copy, RefreshCcw, Trash2, Loader2 } from "lucide-react"

interface ApiKeyData {
  api_key: string
  created_at: string
}

interface ProjectSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // API Key 相关状态
  isLoadingApiKey: boolean
  apiKeyData: ApiKeyData | null
  newlyGeneratedKey: string | null
  isRegenerating: boolean
  isDeleting: boolean
  // API Key 操作
  onCopyApiKey: () => void
  onGenerateApiKey: () => void
  onRegenerateApiKey: () => void
  onDeleteApiKey: () => void
  // 格式化函数
  formatApiKey: (key: string) => string
}

export const ProjectSettingsDialog = memo(({
  open,
  onOpenChange,
  isLoadingApiKey,
  apiKeyData,
  newlyGeneratedKey,
  isRegenerating,
  isDeleting,
  onCopyApiKey,
  onGenerateApiKey,
  onRegenerateApiKey,
  onDeleteApiKey,
  formatApiKey
}: ProjectSettingsDialogProps) => {
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            项目设置
          </DialogTitle>
          <DialogDescription>
            管理项目的 API Key 和其他配置
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* API Key 管理区域 */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">API Key 管理</h3>
            </div>

            {isLoadingApiKey ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : apiKeyData?.api_key ? (
              <div className="space-y-4">
                {/* 显示 API Key */}
                <div className="p-4 bg-muted rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-sm">
                      {newlyGeneratedKey ? (
                        <span className="text-green-600 font-semibold">{newlyGeneratedKey}</span>
                      ) : (
                        formatApiKey(apiKeyData.api_key)
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onCopyApiKey}
                        title="复制 API Key"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onRegenerateApiKey}
                        title="重新生成"
                        disabled={isRegenerating}
                      >
                        <RefreshCcw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onDeleteApiKey}
                        title="删除"
                        disabled={isDeleting}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    创建时间: {new Date(apiKeyData.created_at).toLocaleString('zh-CN')}
                  </div>
                  {newlyGeneratedKey && (
                    <div className="p-2 bg-amber-100 text-amber-800 text-xs rounded">
                      ⚠️ 这是完整的 API Key，请立即复制保存。关闭对话框后将只显示部分内容。
                    </div>
                  )}
                </div>

                {/* 使用说明 */}
                <div className="text-sm text-muted-foreground space-y-2">
                  <p className="font-medium">📖 使用说明</p>
                  <p>API Key 用于让外部系统调用此项目的 AI Agent：</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>第三方系统集成（CRM、ERP 等）</li>
                    <li>移动 App / 小程序后端</li>
                    <li>API 测试工具（Postman 等）</li>
                  </ul>
                  <div className="mt-2 p-2 bg-slate-100 rounded font-mono text-xs overflow-x-auto">
                    <div className="text-slate-500"># 调用示例</div>
                    <div>curl -X POST /api/v1/flow/execute \</div>
                    <div className="pl-4">-H "X-API-Key: {newlyGeneratedKey ? newlyGeneratedKey.slice(0, 20) : 'sk_proj_xxx'}..."</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 space-y-4">
                <div className="text-muted-foreground">
                  <Key className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>当前没有 API Key</p>
                </div>
                  <Button onClick={onGenerateApiKey} disabled={isRegenerating}>
                  {isRegenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      生成中...
                    </>
                  ) : (
                    '生成 API Key'
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
})

ProjectSettingsDialog.displayName = 'ProjectSettingsDialog'